// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * chatStore thread-replay slice (Phase 4 Final Pass).
 *
 * The four methods on this slice share a single responsibility:
 * fetching historical thread state from the backend and writing
 * it into the store.  Extracting them out of ``chatStore.ts``
 * shrinks the main store by ~170 lines and gives the
 * thread-replay concern a peer file under
 * ``frontend/src/stores/chatStore.threadReplay.ts``.
 *
 * The slice is composed into the main store via
 * :data:`StateCreator` so consumer code is unchanged.
 */
import { extractUserFacingMessage } from '@/api/errorMessage';
import { CHAT_REPLAY_RETRY } from '@/config/runtimeConstants';
import type { ChatCheckpoint, ChatReplayResponse, Message, Session, ThreadTask } from '@/types';
import { api } from '@api/client';

import { normalizeThreadMessages, normalizeThreadTasks } from './chatStore.protocol';
import {
  applyUiSnapshotsToMessages,
  attachClarificationCards,
  clarificationCardBlocksFromCheckpoint,
  fetchUiSnapshotMap,
} from './chatStore.uiSnapshotReplay';
import { selectCurrentThreadId } from './chatStore';
import {
  applyThreadHistoryLoaded,
  applyThreadHistoryMissing,
  DEFAULT_DRAFT_ACTOR_MODE,
} from './chatStore.session';
import {
  type ThreadRuntimeView,
  applyHistoryLoadedToThreadView,
  applyThreadSwitchToThreadView,
  createEmptyThreadView,
} from './chatStore.threadView';


/** Public type of the four replay methods. */
export interface ThreadReplayMethods {
  loadThreadHistory: (threadId: string, sessionId: string) => Promise<void>;
  loadThreadTasks: (threadId: string) => Promise<ThreadTask[]>;
  loadThreadCheckpoints: (threadId: string) => Promise<ChatCheckpoint[]>;
  replayCheckpoint: (
    threadId: string,
    checkpointId: string,
    sessionId?: string
  ) => Promise<ChatReplayResponse | null>;
}


/** The full ``ChatState`` shape the slice needs to read/write.
 *
 * Only the fields the replay methods actually touch are listed;
 * the slice is structurally typed against the main store, so
 * a field added to the main store without updating the slice
 * will compile (TypeScript's structural typing).
 */
export interface ThreadReplaySliceState {
  currentSession: Session | null;
  currentThreadId: string | null;
  messages: Message[];
  historicalMessageIds: string[];
  currentThreadView: ThreadRuntimeView | null;
  checkpoints: ChatCheckpoint[];
  checkpointsLoading: boolean;
  replayLoading: boolean;
  lastReplayCheckpointId: string | null;
  currentThreadTasks: ThreadTask[];
  threadTasksByThread: Record<string, ThreadTask[]>;
  missingHistoryBySession: Record<string, true>;
  chatError: string | null;
  backgroundError: string | null;
}


/** StateCreator factory for the thread-replay slice.
 *
 * Composed into the main store via
 * ``...createThreadReplaySlice(set, get, store)``.
 *
 * The ``set`` parameter is typed as ``any`` because the
 * factory is composed into a larger state object: the
 * :class:`StateCreator` type system requires the slice's
 * parameter types to match the full state, but we want the
 * slice to be readable as a peer file.  The runtime contract
 * is correct (the main store passes the real ``set`` / ``get``
 * for the full store); only the TypeScript inference is
 * relaxed.  A future seam could use a generic-parameterised
 * state type to recover the strict typing.
 */
/**
 * Factory function (not a ``StateCreator``) so the main store can
 * pass its own ``set`` / ``get`` without the ``StateCreator``
 * type enforcing a third ``store`` argument.  The slice is
 * structurally typed against the main store; the runtime
 * contract is correct.
 */
export function createThreadReplaySlice(
  set: (partial: any, replace?: boolean) => void,
  get: () => any,
): ThreadReplayMethods {
  return {
  loadThreadHistory: async (threadId, sessionId) => {
    try {
      // B22a：历史加载与轮次快照并行——快照用于回填每轮 ui_blocks/rag 载荷
      // （G7-I 回放缺口）；失败退化为空 Map，不阻断主路径。
      const [{ data }, uiSnapshots] = await Promise.all([
        api.getThreadHistory(threadId),
        fetchUiSnapshotMap(threadId),
      ]);
      const stateSnapshot = get();
      const historySession =
        stateSnapshot.currentSession?.session_id === sessionId
          ? stateSnapshot.currentSession
          : (stateSnapshot as unknown as { sessions: Session[] }).sessions.find(
              (session) => session.session_id === sessionId,
            );
      const fallbackUserPrompt =
        historySession?.message_count === 0 ? (historySession.title ?? undefined) : undefined;
      const messages = attachClarificationCards(
        applyUiSnapshotsToMessages(
          normalizeThreadMessages(data, threadId, { fallbackUserPrompt }),
          uiSnapshots
        ),
        clarificationCardBlocksFromCheckpoint(data)
      );
      const currentSessionActorMode = normalizeActorMode(
        stateSnapshot.currentSession?.actor_mode,
      );
      const resolvedActorMode = currentSessionActorMode ?? DEFAULT_DRAFT_ACTOR_MODE;

      set((state: any) => {
        const sessionPatch = applyThreadHistoryLoaded(state as any, {
          sessionId,
          threadId,
          messages,
          resolvedActorMode,
        });
        const nextView = state.currentThreadView
          ? applyHistoryLoadedToThreadView(state.currentThreadView, {
              sessionId,
              threadId,
              messages,
            })
          : null;
        return {
          ...sessionPatch,
          currentThreadView: nextView,
          ...(nextView && state.currentSession?.session_id === sessionId
            ? { messages: nextView.messages, historicalMessageIds: nextView.historicalMessageIds }
            : {}),
        };
      });
    } catch (error) {
      const status = getHttpStatus(error);
      set((state: any) =>
        applyThreadHistoryMissing(state as any, {
          sessionId,
          status,
          errorMessage: extractUserFacingMessage(error),
        }),
      );
    }
  },

  loadThreadTasks: async (threadId) => {
    if (!threadId) {
      set({ currentThreadTasks: [] } as Partial<ThreadReplaySliceState>);
      return [];
    }

    try {
      const { data } = await api.getThreadTasks(threadId, { limit: 200 });
      // BUG-35：线程序严格归属过滤——lineage/复用任务可能携带其它 thread_id
      // （实测「动量守恒」失败任务混入「抛体运动」线程面板）。
      const tasks = normalizeThreadTasks(data).filter(
        (task: any) => !task?.thread_id || task.thread_id === threadId,
      );
      // CD-3: derive the active thread id through the canonical selector rather
      // than the raw persisted field (can be stale since round 3).
      set((state: any) => ({
        currentThreadTasks:
          selectCurrentThreadId(state) === threadId ? tasks : state.currentThreadTasks,
        threadTasksByThread: {
          ...state.threadTasksByThread,
          [threadId]: tasks,
        },
      }));
      return tasks;
    } catch (error) {
      set({ backgroundError: extractUserFacingMessage(error) });
      return [];
    }
  },

  loadThreadCheckpoints: async (threadId) => {
    if (!threadId) {
      set({ checkpoints: [], checkpointsLoading: false } as Partial<ThreadReplaySliceState>);
      return [];
    }

    try {
      set({ checkpointsLoading: true, chatError: null } as Partial<ThreadReplaySliceState>);
      const { data } = await api.getThreadCheckpoints(threadId);
      const checkpoints = Array.isArray(data?.checkpoints)
        ? (data.checkpoints as ChatCheckpoint[])
        : [];
      set({ checkpoints, checkpointsLoading: false } as Partial<ThreadReplaySliceState>);
      return checkpoints;
    } catch (error) {
      set({
        checkpoints: [],
        checkpointsLoading: false,
        chatError: extractUserFacingMessage(error),
      } as Partial<ThreadReplaySliceState>);
      return [];
    }
  },

  replayCheckpoint: async (threadId, checkpointId, sessionId) => {
    if (!threadId || !checkpointId) return null;

    try {
      set({
        replayLoading: true,
        lastReplayCheckpointId: checkpointId,
        chatError: null,
      } as Partial<ThreadReplaySliceState>);
      const forkThreadId = `${threadId}:fork:${crypto.randomUUID()}`;
      const replayRequest = {
        thread_id: threadId,
        checkpoint_id: checkpointId,
        replay_mode: 'fork' as const,
        fork_thread_id: forkThreadId,
      };
      let data: unknown;
      const maxAttempts = CHAT_REPLAY_RETRY.maxAttempts;
      const baseDelayMs = CHAT_REPLAY_RETRY.baseDelayMs;
      let lastError: unknown;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          ({ data } = await api.replayFromCheckpoint(replayRequest));
          lastError = null;
          break;
        } catch (error) {
          if (!isRecoverableReplayError(error)) {
            throw error;
          }
          lastError = error;
          if (attempt < maxAttempts - 1) {
            const delay = baseDelayMs * Math.pow(2, attempt);
            await new Promise((resolve) => window.setTimeout(resolve, delay));
          }
        }
      }
      if (lastError) throw lastError;
      const payload = (data || null) as ChatReplayResponse | null;
      const replayThreadId =
        payload?.replay_mode === 'fork' &&
        typeof payload.fork_thread_id === 'string' &&
        payload.fork_thread_id.trim()
          ? payload.fork_thread_id.trim()
          : threadId;
      if (payload && sessionId && replayThreadId) {
        set((state: any) => {
          if (state.currentSession?.session_id !== sessionId) {
            return {};
          }
          const nextCurrentSession = state.currentSession
            ? { ...state.currentSession, thread_id: replayThreadId }
            : state.currentSession;
          return {
            currentSession: nextCurrentSession,
            currentThreadId: replayThreadId,
            currentThreadView: applyThreadSwitchToThreadView(
              state.currentThreadView ?? createEmptyThreadView(replayThreadId),
              { threadId: replayThreadId, sessionId },
            ),
          };
        });
        // P4-3（BUG-16）：切换到 fork 线程后的历史加载若失败，回滚主线
        // 视图（thread_id 恢复源线程），避免停留在"空 fork"假死界面。
        try {
          await Promise.all([
            (get() as any).loadThreadHistory(replayThreadId, sessionId),
            (get() as any).loadThreadTasks(replayThreadId),
          ]);
        } catch (historyError) {
          set((state: any) => {
            if (state.currentSession?.session_id !== sessionId) {
              return {};
            }
            const rolledBackSession = state.currentSession
              ? { ...state.currentSession, thread_id: threadId }
              : state.currentSession;
            return {
              currentSession: rolledBackSession,
              currentThreadId: threadId,
              chatError: '分支线程加载失败，已恢复原对话视图，请重试。',
            } as Partial<ThreadReplaySliceState>;
          });
          throw historyError;
        }
      }
      set({ replayLoading: false } as Partial<ThreadReplaySliceState>);
      return payload;
    } catch (error) {
      set({
        replayLoading: false,
        chatError: extractUserFacingMessage(error),
      } as Partial<ThreadReplaySliceState>);
      return null;
    }
  },
};
}


/** Helper: classifies recoverable HTTP errors for the retry loop. */
function isRecoverableReplayError(error: unknown): boolean {
  const status = getHttpStatus(error);
  if (status === null) return true;
  return status === 500 || status === 502 || status === 503 || status === 504;
}


/** Helper: extracts HTTP status from an axios-style error. */
function getHttpStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: { status?: unknown } }).response;
  if (!response || typeof response !== 'object') return null;
  const status = response.status;
  return typeof status === 'number' ? status : null;
}


function normalizeActorMode(raw: unknown): Session['actor_mode'] | undefined {
  return raw === 'teacher' || raw === 'student' ? raw : undefined;
}
